<script setup lang="ts">
const consent = useCookie<'accepted' | 'essential' | null>('cookie-consent', {
  maxAge: 31536000, // 1 year
  sameSite: 'lax'
})

const showBanner = ref(false)
const showSettings = ref(false)

// Granular consent
const analyticsEnabled = ref(true)

// Check on client mount if consent exists
onMounted(() => {
  // Show banner if no consent recorded
  if (!consent.value || (consent.value !== 'accepted' && consent.value !== 'essential')) {
    showBanner.value = true
  } else if (consent.value === 'essential') {
    // Ensure analytics is disabled if previously rejected
    disableAnalytics()
  }
})

function acceptAll() {
  consent.value = 'accepted'
  showBanner.value = false
  enableAnalytics()
}

function acceptEssential() {
  consent.value = 'essential'
  showBanner.value = false
  disableAnalytics()
}

function saveSettings() {
  consent.value = analyticsEnabled.value ? 'accepted' : 'essential'
  showBanner.value = false
  showSettings.value = false

  if (analyticsEnabled.value) {
    enableAnalytics()
  } else {
    disableAnalytics()
  }
}

function enableAnalytics() {
  // Umami respects data-umami-disabled attribute
  document.documentElement.removeAttribute('data-umami-disabled')
}

function disableAnalytics() {
  document.documentElement.setAttribute('data-umami-disabled', 'true')
}

function open() {
  showBanner.value = true
  showSettings.value = true
}

// Allow re-opening settings from parent
defineExpose({ open })
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="showBanner"
        class="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg"
        role="dialog"
        aria-labelledby="cookie-title"
        aria-describedby="cookie-desc"
        aria-modal="true"
      >
        <div class="max-w-4xl mx-auto">
          <template v-if="!showSettings">
            <h2
              id="cookie-title"
              class="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2"
            >
              Cookie Preferences
            </h2>
            <p
              id="cookie-desc"
              class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4"
            >
              We use cookies to provide essential functionality and improve your experience.
              <NuxtLink
                to="/privacy"
                class="underline hover:text-gray-900 dark:hover:text-gray-200"
              >
                Learn more
              </NuxtLink>
            </p>
            <div class="flex flex-wrap gap-2 sm:gap-3">
              <UButton
                color="primary"
                @click="acceptAll"
              >
                Accept All
              </UButton>
              <UButton
                color="neutral"
                variant="outline"
                @click="acceptEssential"
              >
                Essential Only
              </UButton>
              <UButton
                color="neutral"
                variant="ghost"
                @click="showSettings = true"
              >
                Customize
              </UButton>
            </div>
          </template>

          <template v-else>
            <h2 class="text-lg font-semibold mb-4">
              Cookie Settings
            </h2>

            <div class="space-y-4 mb-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">
                    Essential Cookies
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Required for authentication and security
                  </p>
                </div>
                <USwitch
                  disabled
                  :model-value="true"
                />
              </div>

              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">
                    Analytics Cookies
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Help us improve our service (Umami - privacy-focused)
                  </p>
                </div>
                <USwitch v-model="analyticsEnabled" />
              </div>
            </div>

            <div class="flex gap-3">
              <UButton
                color="primary"
                @click="saveSettings"
              >
                Save Preferences
              </UButton>
              <UButton
                color="neutral"
                variant="ghost"
                @click="showSettings = false"
              >
                Back
              </UButton>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
